import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { useDevice, useTelemetry } from '../api/hooks'

const SINCE_OPTIONS = [
  { label: '1 h',  value: '1h'   },
  { label: '6 h',  value: '6h'   },
  { label: '24 h', value: '24h'  },
  { label: '7 d',  value: '168h' },
  { label: '30 d', value: '720h' },
]

const STATUS_STYLES: Record<string, string> = {
  online:   'bg-green-100 text-green-800',
  offline:  'bg-red-100 text-red-800',
  degraded: 'bg-yellow-100 text-yellow-800',
  unknown:  'bg-gray-100 text-gray-600',
}

function formatTick(iso: string, since: string): string {
  const d = new Date(iso)
  // Show time only for short windows, date+time for longer ones
  if (since === '1h' || since === '6h') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  if (since === '24h') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function formatTooltipLabel(iso: string): string {
  return new Date(iso).toLocaleString()
}

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const deviceId = parseInt(id ?? '0', 10)

  const { data, isLoading, isError } = useDevice(deviceId)
  const [metric, setMetric]   = useState('')
  const [since, setSince]     = useState('1h')

  // Use the first available metric as default once device loads
  const activeMetric = metric || data?.metrics[0] || ''

  const {
    data: telem,
    isLoading: telemLoading,
    isError: telemError,
  } = useTelemetry(deviceId, activeMetric, since)

  if (isLoading) return <div className="px-8 py-6 text-sm text-gray-400">Loading…</div>
  if (isError || !data) return (
    <div className="px-8 py-6 text-sm text-red-600">Device not found.</div>
  )

  const { device, metrics } = data
  const chartData = (telem?.points ?? []).map((p: { time: string; value: number }) => ({
    time: p.time,
    value: parseFloat(p.value.toFixed(4)),
  }))

  return (
    <div className="px-8 py-6 space-y-6">
      {/* ── Breadcrumb ─────────────────────────────────────────── */}
      <nav className="text-sm text-gray-400">
        <Link to="/devices" className="hover:text-gray-700">Devices</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-700 font-medium">{device.external_id}</span>
      </nav>

      {/* ── Device header ──────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 font-mono">
            {device.external_id}
          </h1>
          {device.name && (
            <p className="text-sm text-gray-500 mt-0.5">{device.name}</p>
          )}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[device.status] ?? STATUS_STYLES.unknown}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${device.status === 'online' ? 'bg-green-500' : device.status === 'offline' ? 'bg-red-500' : 'bg-gray-400'}`} />
          {device.status}
        </span>
      </div>

      {/* ── Meta cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'First seen', value: new Date(device.first_seen).toLocaleDateString() },
          { label: 'Last seen',  value: new Date(device.last_seen).toLocaleString() },
          { label: 'Type',       value: device.device_type || '—' },
          { label: 'Metrics',    value: metrics.length > 0 ? metrics.length + ' tracked' : 'none yet' },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{label}</p>
            <p className="text-sm font-medium text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {/* ── Telemetry chart ────────────────────────────────────── */}
      {metrics.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center text-gray-400 shadow-sm">
          <div className="text-3xl mb-2">📊</div>
          <p className="text-sm font-medium">No telemetry data yet</p>
          <p className="text-xs mt-1">Data will appear once the device starts sending readings.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
          {/* Controls */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-500">Metric</label>
              <select
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={activeMetric}
                onChange={e => setMetric(e.target.value)}
              >
                {metrics.map((m: string) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-1">
              {SINCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSince(opt.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                    since === opt.value
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="p-5">
            {telemLoading && (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400">
                Loading…
              </div>
            )}
            {telemError && (
              <div className="h-64 flex items-center justify-center text-sm text-red-500">
                Failed to load telemetry.
              </div>
            )}
            {!telemLoading && !telemError && chartData.length === 0 && (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <p className="text-sm">No data in this time window.</p>
                <p className="text-xs mt-1">Try a longer range.</p>
              </div>
            )}
            {!telemLoading && !telemError && chartData.length > 0 && (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={t => formatTick(t, since)}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    width={48}
                  />
                  <Tooltip
                    labelFormatter={(label) => formatTooltipLabel(String(label))}
                    formatter={(v) => [typeof v === 'number' ? v.toFixed(3) : v, activeMetric]}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / .05)',
                    }}
                  />
                  <ReferenceLine y={0} stroke="#e2e8f0" />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {telem && (
            <div className="px-5 pb-4 text-xs text-gray-400">
              {telem.count} data point{telem.count !== 1 ? 's' : ''} · auto-refreshes every 60 s
            </div>
          )}
        </div>
      )}
    </div>
  )
}
